import { type Decimal, isGrownForm, sum, ZERO } from "@bl/domain";
import type { Item } from "../api";
import { formLabel } from "../format";

/**
 * Hops in Inventory, one row per variety (Scott, 2026-10-02): the row lists its forms, drops down to
 * each form's item, and a form opens to its lots, as a malt opens to its item page. An extract or oil
 * of the variety is one of its forms, as the catalog shows it under the hop.
 *
 * A yeast strain is one row the same way once it has been harvested (Tyler, 2026-10-07: "Slurry as its
 * own item under a strain"): what was bought, and its harvested slurry. The two are measured
 * differently, a pitch and a weight, so its row lists each form's amount rather than adding them up.
 */
export interface Variety {
  key: string;
  /** Hops by variety, or a yeast by strain. */
  kind: "hop" | "yeast";
  /**
   * The variety's name, from one of its hops as grown: "Citra", never an oil's "HopBurst® ...". A
   * strain's is the yeast that was bought.
   */
  name: string;
  /** Its forms' items: a hop's in the order the page sorted them, a strain's bought one first. */
  items: Item[];
}

export type InventoryRow = { kind: "item"; item: Item } | { kind: "variety"; variety: Variety };

/**
 * The page's items as rows: each hop variety's items gathered into one row where the first of them
 * stood, and everything else as it was. A variety with no hop as grown, an oil on its own, stays a
 * plain row, since there is no variety row to name it.
 */
export function rowsOf(items: Item[]): InventoryRow[] {
  const varieties = new Map<string, Item[]>();
  const strains = new Map<string, Item[]>();
  for (const i of items) {
    if (i.category === "hop" && i.hopVariety) {
      varieties.set(i.hopVariety, [...(varieties.get(i.hopVariety) ?? []), i]);
    }
    if (i.category === "yeast") {
      const strain = i.strainItemId ?? i.id;
      strains.set(strain, [...(strains.get(strain) ?? []), i]);
    }
  }
  const out: InventoryRow[] = [];
  const placed = new Set<string>();
  for (const i of items) {
    if (i.category === "yeast") {
      const strain = i.strainItemId ?? i.id;
      const forms = strains.get(strain) ?? [i];
      // A strain never harvested, or harvested yeast whose strain is not listed, stays a plain row.
      const bought = forms.find((f) => f.id === strain);
      if (forms.length < 2 || !bought) {
        out.push({ kind: "item", item: i });
        continue;
      }
      const key = `yeast:${strain}`;
      if (placed.has(key)) continue;
      placed.add(key);
      out.push({
        kind: "variety",
        variety: {
          key,
          kind: "yeast",
          name: bought.displayName,
          items: [bought, ...forms.filter((f) => f !== bought)],
        },
      });
      continue;
    }
    const forms = i.category === "hop" && i.hopVariety ? varieties.get(i.hopVariety) : undefined;
    const grown = forms?.find((f) => isGrownForm(f.hopForm));
    if (!forms || !grown || !i.hopVariety) {
      out.push({ kind: "item", item: i });
      continue;
    }
    if (placed.has(i.hopVariety)) continue;
    placed.add(i.hopVariety);
    out.push({
      kind: "variety",
      variety: { key: i.hopVariety, kind: "hop", name: grown.name, items: forms },
    });
  }
  return out;
}

const unique = (values: string[]) => [...new Set(values.filter(Boolean))];

/** Its forms as a list: "T-90, Cryo". */
export const formsOf = (v: Variety) => unique(v.items.map(formNameOf)).join(", ");

/** A form as its row names it: "Cryo", or the oil's own name beside "Hop oil". */
export function formNameOf(i: Item): string {
  const form = formLabel(i) || "Form not given";
  return isGrownForm(i.hopForm) || !i.hopForm ? form : `${form} · ${i.displayName}`;
}

/** The brands on hand across its forms, by short name; a strain's maker. */
export const brandsOf = (v: Variety) =>
  v.kind === "yeast"
    ? (v.items[0]?.manufacturer ?? "")
    : unique(v.items.flatMap((i) => i.onHand.brands)).join(", ");

/** The regions on hand across its forms. */
export const regionsOf = (v: Variety) =>
  unique(v.items.flatMap((i) => i.onHand.regions)).join(", ");

/** Everything on hand across its forms, canonical. Every hop is weighed, so they add up. */
export const totalOf = (v: Variety) => sum(v.items.map((i) => i.onHand.quantity));

/** Each location's stock across its forms, canonical. */
export function byLocationOf(v: Variety): Map<string, Decimal> {
  const out = new Map<string, Decimal>();
  for (const i of v.items) {
    for (const b of i.onHand.byLocation) {
      out.set(b.locationId, (out.get(b.locationId) ?? ZERO).plus(b.quantity));
    }
  }
  return out;
}
